import React, { useEffect, useState } from "react";
import { Search } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import {
  fetchAdminUsers,
  setUserComplimentary,
} from "../features/admin/slice-extensions/admin-slice-extension";
import {
  selectAdminUsers,
  selectAdminUsersStatus,
  selectAdminUsersTotalPages,
  selectAdminUsersTotalCount,
} from "../features/admin/admin-slice";
import { AdminUser } from "../features/admin/models/AdminUser";
import { useChangeLoginEmailModal } from "../features/admin/hooks/useChangeLoginEmailModal";
import { useCustomPriceModal } from "../features/admin/hooks/useCustomPriceModal";
import { LoadingStatus } from "../features/sessions/models/LoadingStatus";
import LeftMenu from "../components/LeftMenu";
import Breadcrumbs from "../components/Breadcrumbs";
import AdminTabs from "../components/admin/AdminTabs";
import AdminUsersList from "../components/admin/AdminUsersList";
import AppReviewDemoPanel from "../components/admin/AppReviewDemoPanel";
import ChangeLoginEmailModal from "../components/admin/ChangeLoginEmailModal";
import ComplimentaryModal from "../components/admin/ComplimentaryModal";
import CustomPriceModal from "../components/admin/CustomPriceModal";

const PAGE_SIZE = 20;

const AdminUsersPage: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const users = useAppSelector(selectAdminUsers);
  const status = useAppSelector(selectAdminUsersStatus);
  const totalPages = useAppSelector(selectAdminUsersTotalPages);
  const totalCount = useAppSelector(selectAdminUsersTotalCount);

  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [complimentaryUser, setComplimentaryUser] = useState<AdminUser | null>(null);
  const [complimentaryBusy, setComplimentaryBusy] = useState(false);
  const {
    customPriceUser,
    customPriceBusy,
    openCustomPrice,
    closeCustomPrice,
    applyCustomPrice,
  } = useCustomPriceModal();
  const {
    emailChangeUser,
    emailChangeBusy,
    openChangeLoginEmail,
    closeChangeLoginEmail,
    applyEmailChange,
  } = useChangeLoginEmailModal();

  // Reset to the first page whenever the search term changes.
  useEffect(() => {
    setPage(1);
  }, [search]);

  // Debounce the search so we fetch once the user pauses typing.
  useEffect(() => {
    const handle = setTimeout(() => {
      dispatch(fetchAdminUsers({ page, pageSize: PAGE_SIZE, search }));
    }, 300);
    return () => clearTimeout(handle);
  }, [dispatch, page, search]);

  const applyComplimentary = async (until: string | null) => {
    if (!complimentaryUser) return;
    setComplimentaryBusy(true);
    try {
      await dispatch(
        setUserComplimentary({ instructorId: complimentaryUser.instructorId, until })
      ).unwrap();
      toast.success(t(until ? "admin.complimentary.granted" : "admin.complimentary.revoked"));
      setComplimentaryUser(null);
    } catch (error) {
      toast.error((error as { message?: string })?.message || t("admin.complimentary.failed"));
    } finally {
      setComplimentaryBusy(false);
    }
  };

  return (
    <section>
      <div className="flex h-full gap-4">
        <LeftMenu />
        <div className="w-full p-4 pb-[120px] lg:px-10 lg:pb-10">
          <div className="lg:max-w-[1280px] 2xl:max-w-[1760px]">
            <Breadcrumbs />
            <div className="space-y-4 py-6 lg:space-y-6">
              <div>
                <h1>{t("admin.title")}</h1>
                <p className="text-grey-40 dark:text-grey-70">{t("admin.subtitle")}</p>
              </div>

              <AdminTabs />

              <AppReviewDemoPanel />

              <div className="relative">
                <Search
                  size={18}
                  className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-grey-50 dark:text-grey-60"
                />
                <input
                  className="input pl-10"
                  type="search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={t("admin.searchPlaceholder")}
                  aria-label={t("admin.searchPlaceholder")}
                />
              </div>

              <AdminUsersList
                users={users}
                isLoading={status === LoadingStatus.Loading}
                isFailed={status === LoadingStatus.Failed}
                totalCount={totalCount}
                totalPages={totalPages}
                page={page}
                onPageChange={setPage}
                onManageComplimentary={setComplimentaryUser}
                onChangeLoginEmail={openChangeLoginEmail}
                onManageCustomPrice={openCustomPrice}
              />
            </div>
          </div>
        </div>
      </div>

      {complimentaryUser && (
        <ComplimentaryModal
          user={complimentaryUser}
          busy={complimentaryBusy}
          onApply={applyComplimentary}
          onClose={() => setComplimentaryUser(null)}
        />
      )}

      {customPriceUser && (
        <CustomPriceModal
          user={customPriceUser}
          busy={customPriceBusy}
          onApply={applyCustomPrice}
          onClose={closeCustomPrice}
        />
      )}

      {emailChangeUser && (
        <ChangeLoginEmailModal
          user={emailChangeUser}
          busy={emailChangeBusy}
          onApply={applyEmailChange}
          onClose={closeChangeLoginEmail}
        />
      )}
    </section>
  );
};

export default AdminUsersPage;
